import React, { memo } from 'react';
import { CheckIcon, PlusCircleIcon } from '@heroicons/react/outline';
import { useStore } from 'context/store-context';
import { useMutation, useQuery } from '@apollo/client';
import { SET_STORE_PLAN, SETUP_STRIPE_CONNECTION } from 'gql/mutations';
import { GET_RETURN_BILLING_PRICING_STATUS } from 'gql/queries';
import { useUser } from 'context/user-context';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import { StoreReturnPricingTable } from 'components/ReturnPricingTable';
import { Loader } from 'components/Loader';
import type { ReturnBillingPricingStatusQueryResult } from 'components/ReturnPricingTable';
import { isMerchNowManagedBilling, usesStripeBilling } from 'utils/storeBillingPolicy';

export enum BILLING_PROVIDER {
  SHOPIFY_BILLING = 'SHOPIFY_BILLING',
  STRIPE_BILLING = 'STRIPE_BILLING',
}

interface StoreBillingProps {}

const StoreBilling: React.FC<StoreBillingProps> = () => {
  const { user } = useUser();
  const { storeId, storeProperties } = useStore();
  const currentStore = storeId && storeProperties?.id === storeId ? storeProperties : undefined;
  const managedBilling = isMerchNowManagedBilling(currentStore);
  const stripeBilling = usesStripeBilling(currentStore);

  const [billingSelection, setBillingSelection] =
    React.useState<{ storeId?: string; provider: BILLING_PROVIDER } | null>(null);
  const billingProvider = billingSelection?.storeId === storeId ? billingSelection?.provider : null;
  const setBillingProvider = (provider: BILLING_PROVIDER) => setBillingSelection({ storeId, provider });
  const [billingEmail, setBillingEmail] = React.useState({ storeId, value: '' });
  const stripeBillingEmail = billingEmail.storeId === storeId ? billingEmail.value : '';
  const { data: returnBillingPricingStatusData } =
    useQuery<ReturnBillingPricingStatusQueryResult>(
      GET_RETURN_BILLING_PRICING_STATUS,
      {
        variables: { storeId },
        skip: !storeId,
        fetchPolicy: 'network-only',
      },
    );
  const returnedBillingPricingStatus =
    returnBillingPricingStatusData?.returnBillingPricingStatus;
  const returnBillingBlockedReason =
    returnedBillingPricingStatus?.storeId === storeId
      ? returnedBillingPricingStatus?.blockedReason ?? null
      : null;
  const requiresShopifyReauthorization =
    returnBillingBlockedReason === 'SHOPIFY_BILLING_REAUTHORIZATION_REQUIRED';
  const requiresStripeReconnect =
    returnBillingBlockedReason === 'STRIPE_BILLING_CONNECTION_REQUIRED';
  const isShopifyBillingConnected = !!currentStore?.plan?.chargeConfirmed;
  const isStripeBillingConnected = !!currentStore?.billing;

  const isBillingConnected = stripeBilling
    ? isStripeBillingConnected && !requiresStripeReconnect
    : isShopifyBillingConnected && !requiresShopifyReauthorization;
  const stripeStore = currentStore?.store;

  const [setStorePlan] = useMutation(SET_STORE_PLAN, {
    onCompleted: (data) => {
      window.location.href = data?.setStorePlan?.chargeUrl;
    },
    onError: (error) => {},
  });

  const [setupStripeConnection] = useMutation(SETUP_STRIPE_CONNECTION, {
    onCompleted: (data) => {
      window.location.href = data.setupStripeConnection;
    },
  });

  const handleStripeBillingEmail = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setBillingEmail({ storeId, value: event.target.value });
  };

  const handleSetupStripeConnection = () => {
    const email = stripeBillingEmail || user?.email;

    if (managedBilling || !stripeBilling || !stripeStore || !email) return;

    setupStripeConnection({
      variables: {
        store: stripeStore,
        email,
      },
    });
  };

  const stripeConnectionDisabled =
    isBillingConnected || !stripeBillingEmail || !stripeStore;

  if (!currentStore) {
    return <SettingsViewWrapper activeNav="billing/subscription" sectionId="billing"><Loader /></SettingsViewWrapper>;
  }

  if (managedBilling) {
    return (
      <SettingsViewWrapper activeNav="billing/subscription" sectionId="billing">
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900">Stripe Billing</h1>
          <p className="mt-2 text-sm text-gray-600">Billing is managed through your MerchNow account.</p>
          <StoreReturnPricingTable storeId={storeId} className="mt-6" />
        </section>
      </SettingsViewWrapper>
    );
  }

  return (
    <SettingsViewWrapper activeNav="billing/subscription" sectionId="billing">
      <div className="w-full h-full bg-[#f6f6f7]">
        <div className="max-w-3xl mx-auto py-14 px-6">
          <div className="flex flex-col md:flex-row gap-6 items-start mb-10">
            <div className="flex-1">
              <h1 className="text-[28px] font-semibold text-[#202223] leading-tight mb-2">
                Choose How You&apos;d Like to Be Billed
              </h1>

              <p className="text-[#6d7175] text-sm max-w-2xl leading-relaxed">
                No monthly fees, no onboarding costs, no commitment. Our pricing
                is{' '}
                <strong className="text-[#202223]">
                  100% performance-based
                </strong>{' '}
                — only pay when you earn.{' '}
                <a
                  href="https://www.shipaid.com/pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2563EB] hover:text-[#1d4ed8] hover:underline"
                >
                  (learn how it works)
                </a>
              </p>
            </div>

            <div className="flex-shrink-0">
              <div
                className="w-[200px]"
                dangerouslySetInnerHTML={{
                  __html: `<div style="position: relative; box-sizing: content-box; width: 100%; aspect-ratio: 2.1201353637901863;"><iframe src="https://guides.shipaid.com/embed/cmcwg5r0l1gfz9empn5rrj64z?embed_v=2" loading="lazy" title="Choose How You'd Like to Be Billed" allow="clipboard-write" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 200px; height: 150px"></iframe></div>`,
                }}
              />
            </div>
          </div>

          <StoreReturnPricingTable storeId={storeId} className="mb-8" />

          <div className="grid md:grid-cols-2 gap-8">
            <div
              onClick={() => {
                if (!stripeBilling) {
                  setBillingProvider(BILLING_PROVIDER.SHOPIFY_BILLING);
                }
              }}
              className={`bg-white p-6 rounded-2xl shadow-sm border transition-all cursor-pointer ${
                billingProvider === BILLING_PROVIDER.SHOPIFY_BILLING
                  ? 'border-[#2563EB] shadow-lg -translate-y-1'
                  : 'border-[#e1e3e5] hover:shadow-lg hover:-translate-y-1 hover:border-[#c9cccf]'
              } ${
                stripeBilling
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/shopify-icon.png"
                  alt="shopify-billing"
                  className="w-12 h-12"
                />
                <h2 className="text-xl font-semibold text-[#202223]">
                  Shopify Billing
                </h2>
              </div>
              <p className="text-[#6d7175] text-sm mb-4">
                Billing through your Shopify admin. Fast, seamless, and
                integrated.
              </p>
              <ul className="space-y-2 text-[#202223] text-sm mb-6">
                <li className="flex gap-2">
                  <span className="text-[#008060] font-bold">✓</span>
                  Integrated with Shopify
                </li>
                <li className="flex gap-2">
                  <span className="text-[#008060] font-bold">✓</span>
                  Unified billing dashboard
                </li>
                <li className="flex gap-2">
                  <span className="text-[#008060] font-bold">✓</span>
                  Automatic currency conversion
                </li>
              </ul>
              {billingProvider === BILLING_PROVIDER.SHOPIFY_BILLING ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (requiresShopifyReauthorization && storeId) {
                      setStorePlan({ variables: { storeId } });
                    } else if (currentStore.plan?.chargeUrl) {
                      window.location.href = currentStore.plan.chargeUrl;
                    } else if (storeId) {
                      setStorePlan({ variables: { storeId } });
                    }
                  }}
                  disabled={isBillingConnected}
                  className={`w-full py-3 rounded-xl font-semibold transition ${
                    isBillingConnected
                      ? 'bg-[#008060] text-white cursor-default'
                      : 'bg-[#202223] text-white hover:bg-[#2e2e2e]'
                  }`}
                >
                  {requiresShopifyReauthorization ? (
                    'Review Shopify billing authorization'
                  ) : isBillingConnected ? (
                    <>
                      <CheckIcon className="inline mr-2 w-5 h-5" />
                      Billing Authorized
                    </>
                  ) : (
                    'Continue with Shopify Billing'
                  )}
                </button>
              ) : (
                <button
                  className="w-full bg-[#202223] text-white py-3 rounded-xl font-semibold hover:bg-[#2e2e2e] transition"
                  disabled={stripeBilling}
                >
                  Continue with Shopify Billing
                </button>
              )}
            </div>

            <div
              onClick={() => {
                if (stripeBilling) {
                  setBillingProvider(BILLING_PROVIDER.STRIPE_BILLING);
                }
              }}
              className={`bg-white p-6 rounded-2xl shadow-sm border transition-all cursor-pointer ${
                billingProvider === BILLING_PROVIDER.STRIPE_BILLING
                  ? 'border-[#2563EB] shadow-lg -translate-y-1'
                  : 'border-[#e1e3e5] hover:shadow-lg hover:-translate-y-1 hover:border-[#c9cccf]'
              } ${
                stripeBilling
                  ? ''
                  : 'opacity-50 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/stripe-icon.png"
                  alt="stripe-billing"
                  className="w-12 h-12"
                />
                <h2 className="text-xl font-semibold text-[#202223]">
                  Stripe Billing
                </h2>
              </div>
              <p className="text-[#6d7175] text-sm mb-4">
                More billing control with analytics, flexibility, and global
                payment options.
              </p>
              <ul className="space-y-2 text-[#202223] text-sm mb-6">
                <li className="flex gap-2">
                  <span className="text-[#2563EB] font-bold">✓</span>
                  Advanced analytics
                </li>
                <li className="flex gap-2">
                  <span className="text-[#2563EB] font-bold">✓</span>
                  Multiple payment methods
                </li>
                <li className="flex gap-2">
                  <span className="text-[#2563EB] font-bold">✓</span>
                  Detailed reporting
                </li>
              </ul>
              {billingProvider === BILLING_PROVIDER.STRIPE_BILLING ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSetupStripeConnection();
                  }}
                  disabled={stripeConnectionDisabled}
                  className={`w-full py-3 rounded-xl font-semibold transition ${
                    isBillingConnected
                      ? 'bg-[#2563EB] text-white cursor-default'
                      : stripeConnectionDisabled
                      ? 'bg-[#c9cccf] text-[#6d7175] cursor-not-allowed'
                      : 'bg-[#2563EB] text-white hover:bg-[#1d4ed8]'
                  }`}
                >
                  {requiresStripeReconnect ? (
                    'Continue to reconnect Stripe billing'
                  ) : isBillingConnected ? (
                    <>
                      <CheckIcon className="inline mr-2 w-5 h-5" />
                      Stripe Connected
                    </>
                  ) : (
                    'Continue with Stripe Billing'
                  )}
                </button>
              ) : (
                <button
                  className="w-full bg-[#2563EB] text-white py-3 rounded-xl font-semibold hover:bg-[#1d4ed8] transition"
                  disabled={!stripeBilling}
                >
                  Continue with Stripe Billing
                </button>
              )}
            </div>
          </div>

          {billingProvider === BILLING_PROVIDER.STRIPE_BILLING && (
            <div className="mt-8 bg-white p-6 rounded-2xl shadow-sm border border-[#e1e3e5]">
              <h6 className="text-lg font-semibold text-[#202223] mb-4">
                Stripe Billing Setup
              </h6>
              <p className="text-[#6d7175] mb-4 leading-relaxed">
                You&apos;re about to securely set up your billing through
                Stripe, a PCI-compliant platform trusted by millions of
                businesses.
              </p>
              <p className="text-[#6d7175] mb-6 leading-relaxed">
                Please enter the email address where you&apos;d like to receive
                billing receipts, subscription updates, and payment
                confirmations.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#202223] mb-2">
                  Billing Notification Email
                </label>
                <input
                  type="email"
                  value={stripeBillingEmail || ''}
                  onChange={handleStripeBillingEmail}
                  placeholder="Eg: your@email.com"
                  className="w-full px-4 py-3 border border-[#c9cccf] rounded-lg focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] transition-all"
                />
              </div>
              <p className="text-sm text-[#6d7175] mb-6">
                This is not used for logging into Stripe. It&apos;s only for
                billing communications.
              </p>
              <button
                onClick={handleSetupStripeConnection}
                disabled={stripeConnectionDisabled}
                className={`w-full py-3 rounded-xl font-semibold transition ${
                  stripeConnectionDisabled
                    ? 'bg-[#c9cccf] text-[#6d7175] cursor-not-allowed'
                    : 'bg-[#2563EB] text-white hover:bg-[#1d4ed8]'
                }`}
              >
                {requiresStripeReconnect ? (
                  <>
                    <PlusCircleIcon className="inline mr-2 w-5 h-5" />
                    Reconnect Stripe billing
                  </>
                ) : isBillingConnected ? (
                  <>
                    <CheckIcon className="inline mr-2 w-5 h-5" />
                    Stripe Connected
                  </>
                ) : (
                  <>
                    <PlusCircleIcon className="inline mr-2 w-5 h-5" />
                    Authorize in Stripe
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </SettingsViewWrapper>
  );
};

export default memo(StoreBilling);
